'use client';

import { c as _c } from "react/compiler-runtime";
import './content-feed.scss';
import React from 'react';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import type { ContentFeedCardProps } from '../content-feed-card/content-feed-card.tsx';
import { ContentFeedCard } from '../content-feed-card/content-feed-card.tsx';
import { useContentFeed } from './content-feed.logic.ts';
import type { ContentFeedEntryResolved, ContentFeedListResponse } from './content-feed.types.ts';

/**
 * Maps an API entry to ContentFeedCard props.
 */
const mapEntryToCardProps = (entry: ContentFeedEntryResolved): ContentFeedCardProps => {
  const props: ContentFeedCardProps = {
    sourceType: entry.source,
    title: entry.title || '',
    description: entry.description || undefined,
    media: entry.media,
    publishedAt: entry.publishedAt,
    author: entry.author,
    match: entry.match,
    programCountryIsoCode: entry.programCountry?.isoCode,
    programCountryName: entry.programCountry?.name,
    hasNewUpdate: entry.hasNewUpdate,
    icon: entry.fcp ? 'fcp' : undefined
  };
  if (entry.source === 'causes' && entry.metadata) {
    if (entry.metadata.fundingProgress) {
      props.fundingProgress = entry.metadata.fundingProgress;
    }
    if (entry.metadata.link) {
      props.link = entry.metadata.link;
    }
  }
  return props;
};
export interface ContentFeedProps {
  /** Override the default API fetch function (used for Storybook/testing) */
  fetchOverride?: (params: {
    cursor: string | null;
  }) => Promise<ContentFeedListResponse>;
  /** Heading title for the feed section */
  title?: string;
  /** Subtitle/description below the heading */
  subtitle?: string;
}

/**
 * Content Feed advanced block.
 * Displays a paginated feed of content entries (community, causes, compassion.media)
 * from the Supporter Global Content API.
 */
export const ContentFeed: React.FC<ContentFeedProps> = t0 => {
  const $ = _c(36);
  const {
    fetchOverride,
    title,
    subtitle
  } = t0;
  const {
    entries,
    isLoading,
    hasMore,
    loadMore,
    t
  } = useContentFeed(fetchOverride);
  let t1;
  if ($[0] !== t || $[1] !== title) {
    t1 = title || t("my:contentFeed.heading");
    $[0] = t;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const headingTitle = t1;
  let t2;
  if ($[3] !== subtitle || $[4] !== t) {
    t2 = subtitle || t("my:contentFeed.subtitle");
    $[3] = subtitle;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const headingSubtitle = t2;
  if (isLoading && entries.length === 0) {
    let t3;
    if ($[6] !== headingTitle) {
      t3 = <h2 className="heading-sm heading-neutral-amplified">{headingTitle}</h2>;
      $[6] = headingTitle;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== headingSubtitle) {
      t4 = <p className="body-sm label-neutral-mild">{headingSubtitle}</p>;
      $[8] = headingSubtitle;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t3 || $[11] !== t4) {
      t5 = <div className="ContentFeed__header">{t3}{t4}</div>;
      $[10] = t3;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="ContentFeed__skeleton" aria-busy="true"><Skeleton height={300} borderRadius="var(--outline-radius-lg)" /><Skeleton height={300} borderRadius="var(--outline-radius-lg)" /></div>;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t5) {
      t7 = <div className="ContentFeed max-w-md flex flex-col gap-comfy-xl">{t5}{t6}</div>;
      $[14] = t5;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    return t7;
  }
  if (entries.length === 0) {
    return null;
  }
  let t3;
  if ($[16] !== headingTitle) {
    t3 = <h2 className="heading-sm heading-neutral-amplified">{headingTitle}</h2>;
    $[16] = headingTitle;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== headingSubtitle) {
    t4 = <p className="body-sm label-neutral-mild">{headingSubtitle}</p>;
    $[18] = headingSubtitle;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== t3 || $[21] !== t4) {
    t5 = <div className="ContentFeed__header">{t3}{t4}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== entries) {
    t6 = entries.map(_temp);
    $[23] = entries;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== t6) {
    t7 = <div className="ContentFeed__list">{t6}</div>;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== hasMore || $[28] !== isLoading || $[29] !== loadMore || $[30] !== t) {
    t8 = hasMore && <div className="ContentFeed__load-more" aria-live="polite"><ButtonStyle variant="secondary" size="lg"><button type="button" onClick={loadMore} disabled={isLoading}>{isLoading ? t("my:contentFeed.loading") : t("my:contentFeed.loadMore")}</button></ButtonStyle></div>;
    $[27] = hasMore;
    $[28] = isLoading;
    $[29] = loadMore;
    $[30] = t;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== t5 || $[33] !== t7 || $[34] !== t8) {
    t9 = <div className="ContentFeed max-w-md flex flex-col gap-comfy-xl">{t5}{t7}{t8}</div>;
    $[32] = t5;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
};
function _temp(entry) {
  return <ContentFeedCard key={entry.id} {...mapEntryToCardProps(entry)} />;
}